/*
  Generic "shrink this element's natural size to fit the space left in its
  frame" helper for homepage full-page-scroll sections that are too tall by
  design to just be capped with height:100dvh (Product Hero's image/wordmark
  composition, Product Grid's tile grid) — rather than hand-tuning their
  internal spacing to an assumed viewport size, this measures the real
  rendered height and scales it down uniformly, so every already-tuned
  internal proportion (image crop, wordmark position, tile padding, etc.)
  stays exactly as designed, just smaller.

  Markup contract:
  - the element to shrink gets [data-fit-target]
  - its available height = (its own parent element's clientHeight) minus the
    target's own offset from that parent's top, so content placed above the
    target within the same parent (e.g. a section heading) is respected —
    only the target itself shrinks into whatever space is left beneath it.
  - transform-origin is left to each section's own CSS (top center to anchor
    under a heading, center center to shrink toward the middle of an
    otherwise-empty frame) — only matters for the shrink case below, since
    translateY (the fits-already case) is unaffected by transform-origin.

  No-op by default: on any template/section where the parent isn't height-
  constrained (i.e. everywhere outside the homepage's snap sections), the
  parent's natural height already equals the target's natural height, so
  available == naturalHeight and the centering translate below resolves to
  0 — nothing visibly changes.
*/
(function () {
  function fit(target) {
    var frame = target.parentElement;
    if (!frame) return;

    target.style.transform = "none";
    var naturalHeight = target.getBoundingClientRect().height;
    if (!naturalHeight) return;

    var offsetInFrame =
      target.getBoundingClientRect().top - frame.getBoundingClientRect().top;
    var available = frame.clientHeight - offsetInFrame;

    if (naturalHeight <= available) {
      // Already fits — no shrink needed, but left as-is this stays pinned
      // to the frame's top edge with dead space below on any section
      // shorter than the viewport (e.g. New Arrivals). Center it in the
      // leftover space instead, same as a shrunk section ends up looking
      // (see below) once it's scaled to fit exactly.
      var centerOffset = (available - naturalHeight) / 2;
      target.style.transform = "translateY(" + centerOffset + "px)";
    } else {
      var scale = Math.max(0.3, available / naturalHeight);
      target.style.transform = "scale(" + scale + ")";
    }

    // Chromium has a known quirk where a transform applied via JS to an
    // element sitting inside an `overflow: hidden` + scroll-snap ancestor
    // (exactly this component's frame — see homepage-scroll-snap.js) is
    // written to the DOM correctly but doesn't get composited/painted on
    // first load — it only catches up once something else (e.g. the user
    // scrolling away and back) forces a repaint. That produced the "hero
    // carousel is cut off until I scroll down then up" bug. Reading
    // offsetHeight forces the browser to flush layout/paint synchronously
    // right after the transform is set, instead of leaving it to whenever
    // the next repaint happens to be triggered.
    void target.offsetHeight;
  }

  var pending = false;
  function fitAll() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      document.querySelectorAll("[data-fit-target]").forEach(fit);
    });
  }

  document.addEventListener("DOMContentLoaded", fitAll);
  window.addEventListener("load", fitAll);
  window.addEventListener("resize", fitAll);

  // Shopify's theme editor injects/replaces each section's markup via its
  // Section Rendering API — on first opening the editor, and again on every
  // settings change — which swaps in a brand-new [data-fit-target] node
  // *after* DOMContentLoaded/load have already fired once. Without this,
  // that fresh node never gets fit until something unrelated happens to
  // trigger a window resize.
  document.addEventListener("shopify:section:load", fitAll);

  // Belt-and-braces for outside the editor too: refit whenever a fit
  // target's own frame actually changes size, rather than only at the
  // handful of moments above. Covers late web-font swaps, sibling content
  // reflowing the frame, and any other post-load layout shift.
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(fitAll);
    var observedFrames = new WeakSet();
    var observeFrames = function () {
      document.querySelectorAll("[data-fit-target]").forEach(function (target) {
        var frame = target.parentElement;
        if (frame && !observedFrames.has(frame)) {
          observedFrames.add(frame);
          ro.observe(frame);
        }
      });
    };
    document.addEventListener("DOMContentLoaded", observeFrames);
    window.addEventListener("load", observeFrames);
    document.addEventListener("shopify:section:load", observeFrames);
  }
})();
